import type { Quote, QuoteLine, QuoteStatus, QuoteTotals } from '../types/quote'

const currency = new Intl.NumberFormat('da-DK', {
  style: 'currency',
  currency: 'DKK',
  maximumFractionDigits: 2,
})

export function formatKr(amount: number): string {
  if (!Number.isFinite(amount)) return currency.format(0)
  return currency.format(amount)
}

export function lineSubtotal(line: QuoteLine): number {
  const quantity = Number.isFinite(line.quantity) ? line.quantity : 0
  const price = Number.isFinite(line.unitPrice) ? line.unitPrice : 0
  return quantity * price
}

export function lineTotal(line: QuoteLine): number {
  return lineSubtotal(line) * (1 + (line.vatRate || 0) / 100)
}

export function calculateTotals(lines: QuoteLine[]): QuoteTotals {
  const subtotal = lines.reduce((sum, line) => sum + lineSubtotal(line), 0)
  const vatAmount = lines.reduce((sum, line) => sum + lineSubtotal(line) * ((line.vatRate || 0) / 100), 0)
  return { subtotal, vatAmount, total: subtotal + vatAmount }
}

/**
 * Et sendt tilbud, hvis gyldighedsdato er overskredet, vises som "Udløbet".
 * Accepterede, afviste og kladder påvirkes ikke.
 */
export function effectiveStatus(quote: Quote): QuoteStatus {
  if (quote.status !== 'Sendt' || !quote.validUntil) return quote.status
  const today = new Date().toISOString().slice(0, 10)
  return quote.validUntil < today ? 'Udløbet' : 'Sendt'
}

export function emptyLine(): QuoteLine {
  return {
    id: `line-${Math.random().toString(36).slice(2, 9)}`,
    description: '',
    quantity: 1,
    unit: 'stk.',
    unitPrice: 0,
    vatRate: 25,
  }
}

/** Standard gyldighed: 30 dage fra tilbudsdatoen. */
export function defaultValidUntil(from: string): string {
  const date = from ? new Date(from) : new Date()
  if (Number.isNaN(date.getTime())) return ''
  date.setDate(date.getDate() + 30)
  return date.toISOString().slice(0, 10)
}

export function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Standardtekster på et nyt tilbud. De kopieres ind på tilbuddet ved
 * oprettelse, så de kan redigeres individuelt — PDF-skabelonen indeholder
 * ingen fast kundetekst.
 */
export const defaultQuoteTexts = {
  intro: 'Tak for din henvendelse. Herunder finder du mit tilbud på den aftalte opgave.',
  terms:
    'Tilbuddet er gældende til den anførte dato. Uforudsete opgaver aftales altid med dig, inden de udføres.',
  closing: 'Har du spørgsmål til tilbuddet, er du altid velkommen til at kontakte mig.',
  paymentTerms: 'Betaling 8 dage netto efter udført arbejde.',
}
